import React, { useState, useEffect } from "react";
import _ from 'lodash';
import { useTheme } from '@mui/styles';
import { getUrl, postUrl } from '@utils/ApiAction';
import { currencyFormat } from '@utils/Tools';
import { useTranslation } from 'react-i18next';
import { FormControl, Select, MenuItem, Typography, TextField, InputAdornment, IconButton, Button } from '@mui/material';
import useNotificationLoading from '@utils/useNotificationLoading';

import { MdVisibility, MdVisibilityOff } from "react-icons/md";

const INITIAL_STATE = { 'extend_id': '', 'security_password': '', 'combination_id': '', 'plan_history_id': '' };

const CheckoutDesign = ({ state, info, inputErrors, showPassword, handleChange, handleClickShowPassword, t, i18n, theme, extendPlan , navigateSecurityPassword , password }) => {
    return (
        <div className='flex-c-l flex-col w-full' style={{ gap: 10 }}>
            <div className='p-b-10'>
                <p className='fs-16 txt-upper'>{t('general.pleaseSelect')}</p>

                <FormControl fullWidth>
                    <Select
                        value={state.extend_id || ""}
                        name="extend_id"
                        onChange={handleChange}
                        disabled= {_.size(info?.packages) > 1 ? false: true}
                    >
                        {_.map(info.packages, (_package) => (
                            <MenuItem key={_package?.id} value={_package?.id}>{_package.name[i18n.resolvedLanguage] ?? _package.name['en']}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
            <div className='p-b-10'>
                <p className='fs-16' style={{ fontWeight: 600 }}>{t('package.paymentMethod')}</p>
                <FormControl fullWidth disabled>
                    <Select
                        id="combination_id"
                        value={state.combination_id || ""}
                        name="combination_id"
                        onChange={handleChange}
                    >
                        {
                            _.map(info.payment_methods, (wallets, combination_id) => {
                                return (
                                    <MenuItem key={combination_id} value={combination_id}>
                                        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                            {
                                                _.map(wallets, (percent, wallet_id) => {
                                                    let wallet = info.wallets.find((element) => element?.id == wallet_id);
                                                    return (
                                                        <Typography key={combination_id + wallet?.id} value={combination_id} className='cl-white p-r-5'>
                                                            {' ' + (wallet?.name[i18n.resolvedLanguage] ?? wallet?.name['en']) + ' (' + percent + '%)'}
                                                        </Typography>
                                                    )
                                                })
                                            }
                                        </div>
                                    </MenuItem>
                                )
                            })
                        }
                    </Select>
                </FormControl>
            </div>
            <div className='p-b-10'>
                <p>{t('user.securityPassword')}</p>
                <TextField
                    variant="outlined"
                    fullWidth
                    name='security_password'
                    value={state?.security_password || ''}
                    placeholder={t('user.securityPassword')}
                    onChange={handleChange}
                    type={showPassword?.securityPassword ? 'text' : 'password'}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => handleClickShowPassword('securityPassword')}>
                                    {showPassword?.securityPassword ? <MdVisibility className="img-style" style={{ color: theme.palette.inputIconColor }} /> : <MdVisibilityOff className="img-style" style={{ color: theme.palette.inputIconColor }} />}
                                </IconButton>
                            </InputAdornment>
                        ),
                        disableUnderline: true
                    }}
                    helperText={inputErrors && inputErrors.security_password ? inputErrors.security_password : ''}
                    error={inputErrors && inputErrors.security_password ? true : false}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                        autoComplete: 'new-password'
                    }}
                />
            </div>
            <div className='w-full p-t-20 fs-18 cl-theme flex-sb-m '>
                <p><b>{t('package.totalPrice') + ': '}</b></p>
                {(_.size(info?.packages) > 0 && state.extend_id) &&
                    <p className='cl-theme'><b>{'$ ' + currencyFormat(_.find(info?.packages, { id: state.extend_id })?.price, 2)}</b></p>
                }
            </div>
            {
                password ?
                    <div className='p-t-24'>
                        <Button variant='contained' onClick={navigateSecurityPassword}>{t('user.setSecurityPassword')}</Button>
                    </div>
                    :
                    <div>
                        <Button variant="contained" onClick={extendPlan}>{t('button.purchase')}</Button>
                    </div>
            }
        </div>
    )
}

const SuccessPayDesign = ({ t }) => {
    return (
        <div class="flex-c-m flex-col">
            <img src="/logo-text-white.png" style={{ height: 50 }} alt="logo" />
            <p className='fs-18 p-t-24 cl-theme' style={{ fontWeight: 600 }}>{t('package.success')}</p>
        </div>
    )
}

const ExtendPlan = (props) => {
    let { history, getData } = props;
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const { addAlert, setLoading } = useNotificationLoading();
    const [state, setState] = useState(INITIAL_STATE);
    const [info, setInfo] = useState({});
    const [step, setStep] = useState(0);
    const [inputErrors, setInputErrors] = useState({});
    const [showPassword, setShowPassword] = useState({
        securityPassword: false,
    });
    const [password , setPassword] = useState(false);

    const handleChange = ({ target }) => {
        const { name, value } = target;
        setState({ ...state, [name]: value });
    }

    const handleClickShowPassword = (value) => {
        setShowPassword({
            ...showPassword,
            [value]: !showPassword[value]
        });
    };

    useEffect(() => {
        getExtendPlanSetting()
        setState(prev => ({
            ...prev,
            'plan_history_id': history?.id,
        }))
        // eslint-disable-next-line
    }, [history?.id]);

    useEffect(() => {
        getExtendPlanSetting()
        // eslint-disable-next-line
    }, []);

    const getExtendPlanSetting = () => {
        setLoading(true);
        getUrl('/extend-plan/setting', { 'plan_id': history?.plan_id }).then(response => {
            if (response.status) {
                let combination_id = '';
                if (Object.keys(response?.data?.payment_methods).length > 0) {
                    combination_id = Object.keys(response?.data?.payment_methods)[0];
                }
                setState(prev => ({
                    ...prev,
                    extend_id: response.data.packages[0]?.id,
                    combination_id: combination_id
                }));
                setInfo(response.data);
            }
        }).catch(error => {
            addAlert('', error.message || t('error.contactSupport'), 'error', '');
        }).finally(() => {
            setLoading(false);
        });
    }

    const extendPlan = () => {
        setLoading(true);
        setInputErrors({});
        postUrl('/extend-plan', state).then(response => {
            if (response.status) {
                addAlert('', t('success.purchaseSuccess'), 'success', '');
                setStep(1);
                getData();
            } else {
                setInputErrors(response.errors);
                setPassword(response?.set_password)
                let msg = response.message;
                _.map(response.errors, (value, index) => {
                    msg += "\n " + value;
                })
                addAlert('', msg || response.message, 'error', '');
            }
        }).catch(error => {
            addAlert('', error.message || t('error.contactSupport'), 'error', '');
        }).finally(() => {
            setLoading(false);
        });
    }

    const navigateSecurityPassword = () => {
        window.open('/setting/security-password','_blank');
        setPassword(false);
    }

    const renderContent = (step) => {
        switch (step) {
            case 1: return <SuccessPayDesign t={t} />;
            default: return (
                <CheckoutDesign
                    state={state}
                    info={info}
                    inputErrors={inputErrors}
                    showPassword={showPassword}
                    handleChange={handleChange}
                    handleClickShowPassword={handleClickShowPassword}
                    t={t}
                    i18n={i18n}
                    theme={theme}
                    extendPlan={extendPlan}
                    password={password}
                    navigateSecurityPassword={navigateSecurityPassword}
                />
            );
        }
    }
    return (
        <div>
            {renderContent(step)}
        </div>
    )
}

export default ExtendPlan;